import React, { useState } from 'react';
import { Send, Mail, ArrowUpRight, CheckCircle2, AlertCircle, ChevronDown, CalendarDays, Clock, Video } from 'lucide-react';
import { toast } from 'sonner';
import { siteConfig, services } from '../data/mock';
import { WhatsAppIcon, TelegramIcon, Avatar } from './Brand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const empty = { name: '', email: '', subject: '', service: '', message: '' };

const ContactSection = () => {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error('Request failed');
      setStatus('success');
      setForm(empty);
      toast.success("Message sent — I'll reply within a few hours.");
    } catch {
      setStatus('idle');
      setError('Something went wrong. Please try WhatsApp or Telegram instead.');
      toast.error('Could not send your message.');
    }
  };

  const channels = [
    { id: 'whatsapp', label: 'WhatsApp', value: siteConfig.phone, href: `https://wa.me/${siteConfig.whatsapp}`, icon: <WhatsAppIcon className="w-4 h-4" />, color: 'bg-[#25D366]' },
    { id: 'telegram', label: 'Telegram', value: `@${siteConfig.telegram}`, href: `https://t.me/${siteConfig.telegram}`, icon: <TelegramIcon className="w-4 h-4" />, color: 'bg-[#229ED9]' },
    { id: 'email', label: 'Email', value: siteConfig.email, href: `mailto:${siteConfig.email}`, icon: <Mail className="w-4 h-4" />, color: 'bg-ink' },
    { id: 'freelancer', label: 'Freelancer.com', value: 'Hire via Freelancer', href: siteConfig.freelancerProfile, icon: <ArrowUpRight className="w-4 h-4" />, color: 'bg-green' }
  ];

  return (
    <section id="contact" className="py-24 lg:py-32 bg-paper-2 border-t border-[#E2E8F0] relative overflow-hidden" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mb-12" data-reveal>
          <p className="eyebrow mb-4">Contact · Compose</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-ink leading-[1.05] mb-4">
            Let's get your next campaign <span className="mark">delivered.</span>
          </h2>
          <p className="text-slate text-base md:text-lg leading-relaxed">
            Book a call or send a message. Either way you'll get a clear plan, a timeline and a fixed quote.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-7 window" data-reveal data-testid="contact-form-card">
            <div className="window-bar">
              <span className="window-dot" /><span className="window-dot" /><span className="window-dot" />
              <span className="ml-3 font-mono text-[11px] text-slate-2 tracking-wider">NEW MESSAGE · TO: USAMA</span>
              <span className="ml-auto pill pill-green">replies in hours</span>
            </div>

            {status === 'success' ? (
              <div className="py-16 px-8 text-center" data-testid="contact-success">
                <span className="w-20 h-20 rounded-full bg-[#ECFDF5] flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green" />
                </span>
                <h3 className="font-display text-2xl font-bold text-ink mb-2">Delivered — message received</h3>
                <p className="text-slate mb-8">Thanks! I'll get back to you within a few hours.</p>
                <button onClick={() => setStatus('idle')} className="btn-outline" data-testid="contact-send-another">Send another message</button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6 sm:p-8 space-y-5" data-testid="contact-form">
                {error && (
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-[#FEF2F2] text-[#B91C1C] text-sm" data-testid="contact-error">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" /> {error}
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="block text-sm font-medium text-ink mb-2">From (your name)</span>
                    <input className="field" placeholder="Jane Cooper" value={form.name} onChange={set('name')} required data-testid="contact-input-name" />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-ink mb-2">Reply-to email</span>
                    <input className="field" type="email" placeholder="jane@company.com" value={form.email} onChange={set('email')} required data-testid="contact-input-email" />
                  </label>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="block text-sm font-medium text-ink mb-2">Subject</span>
                    <input className="field" placeholder="Cold email for our SaaS" value={form.subject} onChange={set('subject')} required data-testid="contact-input-subject" />
                  </label>
                  <label className="block relative">
                    <span className="block text-sm font-medium text-ink mb-2">Thread</span>
                    <select className="field appearance-none pr-10 cursor-pointer" value={form.service} onChange={set('service')} required data-testid="contact-select-service">
                      <option value="">Select a service</option>
                      {services.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
                    </select>
                    <ChevronDown className="w-4 h-4 text-slate-2 absolute right-4 bottom-4 pointer-events-none" />
                  </label>
                </div>
                <label className="block">
                  <span className="block text-sm font-medium text-ink mb-2">Message</span>
                  <textarea className="field min-h-[150px] resize-y" placeholder="Who are you targeting, what's the goal, and what's the timeline?" value={form.message} onChange={set('message')} required data-testid="contact-input-message" />
                </label>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-1">
                  <button type="submit" disabled={status === 'sending'} className="btn-ink sm:min-w-[180px]" data-testid="contact-submit-button">
                    {status === 'sending'
                      ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Sending…</>
                      : <><Send className="w-4 h-4" /> Send message</>}
                  </button>
                  <p className="text-xs text-slate-2">No newsletters, no spam. Your details stay private.</p>
                </div>
              </form>
            )}
          </div>

          <div className="lg:col-span-5 space-y-5" data-reveal style={{ transitionDelay: '120ms' }}>
            <a
              href={siteConfig.calendly}
              target="_blank"
              rel="noopener noreferrer"
              className="panel panel-hover block !bg-[#0B132B] !border-[#0B132B] text-white p-7 relative overflow-hidden"
              data-testid="contact-book-call-card"
            >
              <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#10B981]/30 blur-2xl" />
              <div className="relative flex items-start justify-between mb-6">
                <span className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center"><CalendarDays className="w-5 h-5 text-[#10B981]" /></span>
                <Avatar size="w-12 h-12" />
              </div>
              <h3 className="relative font-display text-2xl font-bold mb-2">Book a 30-minute strategy call</h3>
              <p className="relative text-white/70 text-sm leading-relaxed mb-6">
                Free. We'll look at your target market, current deliverability and what a realistic pipeline looks like.
              </p>
              <div className="relative flex flex-wrap gap-2 mb-6">
                <span className="pill !bg-white/10 !border-white/15 !text-white/80"><Clock className="w-3 h-3" /> 30 min</span>
                <span className="pill !bg-white/10 !border-white/15 !text-white/80"><Video className="w-3 h-3" /> Google Meet</span>
                <span className="pill !bg-white/10 !border-white/15 !text-white/80">Calendly</span>
              </div>
              <span className="relative btn-green w-full">Pick a time <ArrowUpRight className="w-4 h-4" /></span>
            </a>

            <ul className="grid grid-cols-2 gap-3" data-testid="contact-channels">
              {channels.map((c) => (
                <li key={c.id}>
                  <a href={c.href} target="_blank" rel="noopener noreferrer" data-testid={`contact-channel-${c.id}`} className="panel panel-hover bg-white flex items-center gap-3 p-4">
                    <span className={`w-9 h-9 rounded-lg ${c.color} text-white flex items-center justify-center flex-shrink-0`}>{c.icon}</span>
                    <span className="leading-tight min-w-0">
                      <span className="block text-[11px] text-slate-2">{c.label}</span>
                      <span className="block text-sm font-semibold text-ink truncate">{c.value}</span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
